import { useMemo, useRef, useState } from 'react';

import { RestrictToElement } from '@dnd-kit/dom/modifiers';
import { useSortable } from '@dnd-kit/react/sortable';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import DragIndicator from '@mui/icons-material/DragIndicator';
import HighlightAltOutlined from '@mui/icons-material/HighlightAltOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { Box, Tab, Tooltip } from '@mui/material';

import { NameInput } from '@/components/NameInput';
import TooltipIconButton from '@/components/TooltipIconButton';
import { Z_INDEX } from '@/constants/z-index';
import { useRemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { TABNAME_MAX_LENGTH } from '@/features/Dashboard/constants/const';
import { createNameSchema } from '@/features/Dashboard/utils/validateName';

import { MoveMenu } from './MoveMenu';

import type { ItemProps } from '../types';

/**
 * Item component representing a single tab within an app.
 * Supports drag-and-drop reordering and inline name editing.
 */
export const Item = ({
  index,
  item,
  column,
  onRemoveItem,
  container,
  isSingleItem,
  onEdit,
  currentDashboardName,
  currentTabId,
  onTabCreated,
  onRename
}: ItemProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const [moveItemMenuAnchor, setMoveItemMenuAnchor] = useState<HTMLElement | null>(null);

  useSortable({
    id: item.id,
    element: elementRef,
    index,
    handle: handleRef,
    type: isSingleItem ? 'singleItem' : 'item',
    accept: ['item', 'singleItem'],
    group: column,
    modifiers: [RestrictToElement.configure({ element: container.current })]
  });

  const { openRemoveModal } = useRemoveWidgetContext();

  const nameErrors = useMemo(() => {
    const res = createNameSchema('Name', TABNAME_MAX_LENGTH).safeParse(item.name);
    if (res.success) return undefined;
    return res.error;
  }, [item.name]);

  const isEditable =
    (item.type === 'graph' &&
      (item.metadata?.graphType === 'forwardSimple' ||
        item.metadata?.graphType === 'seasonal' ||
        !item.metadata?.graphType)) ||
    item.type === 'table';

  return (
    <Tab
      ref={elementRef}
      disabled
      component='div'
      label={
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title='Drag to reorder tab'>
            <Box
              ref={handleRef}
              className='tab-drag-handle'
              sx={{
                width: 16,
                height: 46,
                p: 0,
                borderRadius: 0,
                backgroundColor: 'gx.response',
                zIndex: Z_INDEX.dragging,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'grab'
              }}
            >
              <DragIndicator sx={{ fontSize: '14px' }} />
            </Box>
          </Tooltip>

          <Box
            sx={{
              maxWidth: '300px',
              '.MuiInputBase-root': {
                height: '46px'
              },
              '.MuiTextField-root': {
                minWidth: 'auto',
                display: 'flex',
                input: {
                  color: 'gx.textDisabled',
                  '&:focus': {
                    color: 'text.primary'
                  },
                  '&:hover': {
                    color: 'text.primary'
                  }
                }
              },
              '.MuiButtonBase-root': {
                display: 'none'
              },
              '.error-message': {
                backgroundColor: 'background.paper',
                px: 0.5,
                mt: 0
              }
            }}
          >
            <NameInput
              error={nameErrors}
              maxLength={TABNAME_MAX_LENGTH}
              value={item.name}
              onChange={onRename}
            />
          </Box>
          <Box
            className='edit-buttons__container'
            sx={{
              display: 'flex',
              ml: 'auto',
              gap: '2px',
              '& button': {
                color: 'text.primary',
                '& svg': {
                  color: 'text.primary'
                }
              }
            }}
          >
            <TooltipIconButton
              sx={{ height: 46 }}
              title='Move to ...'
              onClick={e => {
                setMoveItemMenuAnchor(e.currentTarget);
              }}
            >
              <HighlightAltOutlined />
            </TooltipIconButton>
            {isEditable && (
              <TooltipIconButton
                sx={{ height: 46 }}
                title='Configuration settings'
                onClick={() => {
                  onEdit(item.id);
                }}
              >
                <SettingsOutlined />
              </TooltipIconButton>
            )}
            <TooltipIconButton
              sx={{ height: 46 }}
              title='Remove tab'
              onClick={() => {
                openRemoveModal(() => {
                  onRemoveItem(item.id);
                });
              }}
            >
              <ClearOutlined />
            </TooltipIconButton>
            <MoveMenu
              anchorEl={moveItemMenuAnchor}
              appId={column}
              currentDashboardName={currentDashboardName}
              currentTabId={currentTabId}
              item={item}
              open={Boolean(moveItemMenuAnchor)}
              variant='item'
              onClose={() => {
                setMoveItemMenuAnchor(null);
              }}
              onRemoveFromCurrent={() => {
                onRemoveItem(item.id);
              }}
              onTabCreated={onTabCreated}
            />
          </Box>
        </Box>
      }
      sx={{
        '&.Mui-disabled': {
          pointerEvents: 'auto'
        },
        overflow: 'visible',
        minWidth: '180px',
        maxWidth: 'initial',
        whiteSpace: 'nowrap',
        position: 'relative',
        textTransform: 'initial',
        opacity: '1 !important',
        height: 48,
        p: 0,
        borderWidth: '1px',
        borderStyle: 'dashed !important',
        borderColor: 'primary.main',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: 'row',
        gap: 1,
        color: 'text.primary',
        '&:hover': {
          color: 'inherit'
        },
        outline: '0px solid transparent',
        transition: 'outline-color 120ms ease, outline-width 120ms ease',
        '&[data-tab-drop-over="true"]': {
          outlineColor: 'primary.main',
          outlineWidth: '2px'
        }
      }}
    />
  );
};

export default Item;
